import React, { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Clock, Calendar, MapPin, Globe, Lock, CreditCard } from "lucide-react";
import { AuthContext } from "../../context/auth";
import PrivateEventModal from "../../Modals/privateEventModal/PrivateEventModal.jsx";
import "./ExploreEventCard.css";

const ExploreEventCard = ({ event }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [showPrivateModal, setShowPrivateModal] = useState(false);

  const isAuthenticated = !!user;

  const getEventImage = (images) => {
    if (!images || images.length === 0) return "/placeholder-event.jpg";
    return images[0]?.uri || "/placeholder-event.jpg";
  };

  const extractTimeFromDate = (dateString) => {
    if (!dateString) return null;

    try {
      const date = new Date(dateString);
      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes();

      if (hours === 0 && minutes === 0) {
        return null;
      }

      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return null;
    }
  };

  const getDisplayDateTime = () => {
    if (!event)
      return { displayDate: "", displayTime: "", displayLocation: "" };

    if (event.dateOption === "multiple" && event.multipleDates?.[0]) {
      const sortedDates = [...event.multipleDates].sort((a, b) => {
        const dateA = new Date(a.date?.$date || a.date || a.startDate);
        const dateB = new Date(b.date?.$date || b.date || b.startDate);
        return dateA - dateB;
      });
      const firstDate = sortedDates[0];

      let formattedDate = "N/A";
      if (firstDate.date) {
        const dateStr =
          typeof firstDate.date === "string"
            ? firstDate.date
            : firstDate.date.$date || firstDate.date;

        const dateObj = new Date(dateStr);
        formattedDate = dateObj.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        });
      }

      return {
        displayDate: formattedDate,
        displayTime:
          firstDate.time || extractTimeFromDate(firstDate.date) || "",
        displayLocation:
          firstDate.landmark ||
          firstDate.location ||
          event.eventLocation ||
          event.eventLandmark ||
          "",
      };
    }

    if (event.dateOption === "startEnd") {
      let formattedDate = "";
      if (event.startDate) {
        const dateStr =
          typeof event.startDate === "string"
            ? event.startDate
            : event.startDate.$date || event.startDate;

        const dateObj = new Date(dateStr);
        formattedDate = dateObj.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        });
      }

      return {
        displayDate: formattedDate,
        displayTime:
          event.startTime || extractTimeFromDate(event.startDate) || "",
        displayLocation: event.eventLandmark || event.eventLocation || "",
      };
    }

    let formattedDate = "";
    if (event.eventDate) {
      const dateStr =
        typeof event.eventDate === "string"
          ? event.eventDate
          : event.eventDate.$date || event.eventDate;

      const dateObj = new Date(dateStr);
      formattedDate = dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
    }

    const timeValue =
      event.eventTime ||
      event.startTime ||
      extractTimeFromDate(event.eventDate) ||
      "";

    return {
      displayDate: formattedDate,
      displayTime: timeValue,
      displayLocation: event.eventLandmark || event.eventLocation || "",
    };
  };

  const isEventHost = () => {
    if (!isAuthenticated || !user?._id) return false;

    const eventUserId =
      typeof event.userId === "string"
        ? event.userId
        : event.userId?._id || event.userId?.id;

    return user._id === eventUserId;
  };

  const isAcceptedGuest = () => {
    if (!isAuthenticated || !user?._id) return false;
    if (!event?.invitations || !Array.isArray(event.invitations)) return false;

    return event.invitations.some(
      (inv) =>
        inv.guestId?._id === user._id &&
        inv.status?.toLowerCase() === "accepted",
    );
  };

  const isPaidEvent = (event) => {
    if (!event) return false;

    const hasTickets =
      event.isEntryFeeApplicable && (event.ticketFee?.length ?? 0) > 0;

    const hasEventAttire =
      event.isEventAttireCompulsory &&
      event.isEventAttireAvailable &&
      (event.eventAttire?.asoEbiImages?.length ?? 0) > 0;

    const hasCashGift = event.isCashGiftAccepted;

    return hasTickets || hasEventAttire || hasCashGift;
  };

  const handleViewEvent = (e) => {
    e.stopPropagation();

    if (event.visibility === "private") {
      if (isEventHost() || isAcceptedGuest()) {
        navigate(`/eventManagementDetails/${event._id}`, {
          state: { from: location.pathname },
        });
      } else {
        setShowPrivateModal(true);
      }
    } else {
      navigate(`/eventManagementDetails/${event._id}`, {
        state: { from: location.pathname },
      });
    }
  };

  const handleCardClick = () => {
    if (event.visibility === "private") {
      if (isEventHost() || isAcceptedGuest()) {
        navigate(`/eventManagementDetails/${event._id}`, {
          state: { from: location.pathname },
        });
      } else {
        setShowPrivateModal(true);
      }
    } else {
      navigate(`/eventManagementDetails/${event._id}`, {
        state: { from: location.pathname },
      });
    }
  };

  const { displayDate, displayTime, displayLocation } = getDisplayDateTime();
  const isPublic = event.visibility === "public";
  const isPaid = isPaidEvent(event);

  return (
    <>
      <div className="explore-event-card-container" onClick={handleCardClick}>
        <div className="explore-event-card-section">
          <div className="explore-event-card-image-wrapper">
            <img
              src={getEventImage(event.eventImages)}
              alt={event.eventTitle}
              className="explore-event-card-image"
              onError={(e) => {
                e.target.src = "/placeholder-event.jpg";
              }}
            />
            {isPaid && (
              <div className="explore-event-card-paid-badge-overlay">
                <CreditCard size={14} />
                <span>Paid Event</span>
              </div>
            )}
          </div>

          <div className="explore-event-card-content">
            <div className="explore-event-card-title">{event.eventTitle}</div>

            <div className="explore-event-card-badges">
              <div
                className={`explore-event-card-visibility ${
                  isPublic ? "public" : "private"
                }`}
              >
                {isPublic ? (
                  <>
                    <Globe size={14} />
                    <span>Public</span>
                  </>
                ) : (
                  <>
                    <Lock size={14} />
                    <span>Private</span>
                  </>
                )}
              </div>
              <div className="explore-event-card-category">
                {event.eventType}
              </div>
            </div>
          </div>
        </div>

        <div className="explore-event-card-details">
          {displayDate && displayDate !== "N/A" && displayDate !== "" && (
            <div className="explore-event-card-detail-item">
              <Calendar size={16} />
              <span>{displayDate}</span>
            </div>
          )}
          {displayTime && displayTime !== "N/A" && displayTime !== "" && (
            <div className="explore-event-card-detail-item">
              <Clock size={16} />
              <span>{displayTime}</span>
            </div>
          )}
          {(!displayTime || displayTime === "") && displayDate && (
            <div className="explore-event-card-detail-item explore-event-card-time-tba">
              <Clock size={16} />
              <span style={{ fontStyle: "italic", opacity: 0.7 }}>
                Time TBA
              </span>
            </div>
          )}
        </div>

        <div className="explore-event-card-detail-item explore-event-card-location">
          <MapPin size={22} />
          <span className="explore-event-card-location-text">
            {displayLocation ||
              event.eventLocation ||
              event.eventLandmark ||
              "Location TBA"}
          </span>
        </div>

        <div className="explore-event-event-actions">
          <button
            className="explore-event-btn-view-event"
            onClick={handleViewEvent}
          >
            {isPublic
              ? "View Event"
              : isEventHost() || isAcceptedGuest()
                ? "View Event"
                : "Request Access"}
          </button>
        </div>
      </div>

      {showPrivateModal && (
        <PrivateEventModal
          isOpen={showPrivateModal}
          onClose={() => {
            setShowPrivateModal(false);
          }}
          event={event}
        />
      )}
    </>
  );
};

export default ExploreEventCard;
